import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  additionalExpenses,
  type AdditionalExpenseItem,
} from '@/api/additionalExpenses';
import { goodsReceipts } from '@/api/goodsReceipts';
import { contractors as contractorsApi, orgs, nomenclature as nomApi } from '@/api/catalogs';
import SearchSelect from '@/components/SearchSelect';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DocLayout from '@/shared/components/DocLayout';
import QuickCreateLink from '@/shared/components/QuickCreateLink';
import QuickContractModal from '@/shared/components/QuickContractModal';
import QuickContractorModal from '@/shared/components/QuickContractorModal';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { fmtDocDate, moneyDocActions, vatTail } from '@/shared/utils/paymentFromDoc';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { contractLabel } from '@/shared/utils/labels';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  expense_type: string;
  supplier_doc_number: string;
  supplier_doc_date: string;
  amount_without_vat: string;
  vat_rate_id: string;
  vat_amount: string;
  comment: string;
  settlement_account_code: string;
  project_id: string | null;
  items: AdditionalExpenseItem[];
  source_receipt_id: string;
  distribute_by: 'AMOUNT' | 'QUANTITY';
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  expense_type: 'TRANSPORT',
  supplier_doc_number: '',
  supplier_doc_date: '',
  amount_without_vat: '0',
  vat_rate_id: '',
  vat_amount: '0',
  comment: '',
  settlement_account_code: '',
  project_id: null,
  items: [],
  source_receipt_id: '',
  distribute_by: 'AMOUNT',
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** ч.864: іконка документа для кнопки «додати рядки з надходження». */
function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden
      style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z"
        stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

export default function AdditionalExpenseEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по purchases.receipt.create permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('purchases.receipt.create');
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const docQuery = useQuery({
    queryKey: ['additional-expense', id],
    queryFn: () => additionalExpenses.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'additional-expense', (cid) => additionalExpenses.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  // 2026-08-02: попап вибору надходження + відкладене авто-додавання рядків.
  const [receiptPickerOpen, setReceiptPickerOpen] = useState(false);
  // 0410 (2026-08-09): вкладка «Рахунки обліку» (Дт рядків + Кт шапки).
  const [viewTab, setViewTab] = useState<'lines' | 'accounts'>('lines');
  // 2026-08-09 (рішення власника): фільтр попапа по постачальнику.
  const [pickerSupplier, setPickerSupplier] = useState('');
  const contractorNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of contractorsQ.data?.items ?? []) m.set(c.id, c.name);
    return m;
  }, [contractorsQ.data]);
  const pendingAddRef = useRef<string | null>(null);
  const numberPreview = useNumberPreview('doc.additional_expenses', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  // Договоры выбранного контрагента
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });

  // Поступления для выбора
  const receiptsQ = useQuery({
    queryKey: ['goods-receipts-for-ae', form.organization_id],
    queryFn: () => goodsReceipts.list({
      organization_id: form.organization_id, posted: true, limit: 200,
    }),
    enabled: !!form.organization_id,
  });
  const sourceReceiptQ = useQuery({
    queryKey: ['goods-receipt-full', form.source_receipt_id],
    queryFn: () => goodsReceipts.get(form.source_receipt_id),
    enabled: !!form.source_receipt_id,
  });

  useEffect(() => {
    if (!pendingAddRef.current) return;
    if (sourceReceiptQ.data?.id !== pendingAddRef.current) return;
    pendingAddRef.current = null;
    addAllReceiptItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceReceiptQ.data]);

  // ?receipt=<id> — кнопка «створити дод. витрати» з прихода (2026-07-29).
  const receiptParam = searchParams.get('receipt');
  useEffect(() => {
    if (!isNew || !receiptParam) return;
    setForm((f) => f.source_receipt_id ? f : { ...f, source_receipt_id: receiptParam });
  }, [isNew, receiptParam]);

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id,
        expense_type: d.expense_type,
        supplier_doc_number: d.supplier_doc_number ?? '',
        supplier_doc_date: d.supplier_doc_date ?? '',
        amount_without_vat: String(d.amount_without_vat),
        vat_rate_id: d.vat_rate_id ?? '',
        vat_amount: String(d.vat_amount),
        comment: d.comment ?? '',
        settlement_account_code:
          (d as { settlement_account_code?: string | null })
            .settlement_account_code ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          goods_receipt_item_id: it.goods_receipt_item_id,
          distribution_base: String(it.distribution_base),
          allocated_amount: it.allocated_amount,
          account_code: it.account_code ?? null,
        })),
        source_receipt_id: '',
        distribute_by: (d as { distribute_by?: 'AMOUNT' | 'QUANTITY' }).distribute_by ?? 'AMOUNT',
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), source_receipt_id: EMPTY_FORM.source_receipt_id, supplier_doc_number: EMPTY_FORM.supplier_doc_number, }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  // ч.948 (рішення власника): «створити ↗» біля контрагента і договору
  // відкриває ПОПАП просто на сторінці документа (як у ПКО/РКО).
  const [quickContractor, setQuickContractor] = useState(false);
  const [quickContract, setQuickContract] = useState(false);
  const contractorNameForQuick = (contractorsQ.data?.items ?? []).find((c: { id: string; name: string }) => c.id === form.contractor_id)?.name ?? '';

  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomList.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomList.data]);

  const [riCache, setRiCache] = useState<Record<string, { nom: string; qty: string; amount: string; rcpNum?: string; rcpId?: string; rcpSupplier?: string; rcpAcc?: string }>>({});
  // UX-фікс 2026-07-29: збережений документ — дані рядків прихода приходять
  // ЗБАГАЧЕНИМИ з backend (nomenclature_id/receipt_*) — UUID більше не видно.
  useEffect(() => {
    const items = (docQuery.data?.items ?? []) as Array<{
      goods_receipt_item_id: string;
      nomenclature_id?: string | null;
      receipt_quantity?: string | null;
      receipt_amount?: string | null;
      receipt_id?: string | null;
      receipt_document_number?: string | null;
    }>;
    if (!items.length) return;
    setRiCache((c) => {
      const next = { ...c };
      for (const it of items) {
        if (it.nomenclature_id && !next[it.goods_receipt_item_id]) {
          next[it.goods_receipt_item_id] = {
            nom: it.nomenclature_id,
            qty: it.receipt_quantity ?? '',
            amount: it.receipt_amount ?? '',
            rcpNum: it.receipt_document_number ?? undefined,
            rcpId: it.receipt_id ?? undefined,
            rcpSupplier: (it as { receipt_contractor_name?: string | null })
              .receipt_contractor_name ?? undefined,
            rcpAcc: (it as { receipt_account_code?: string | null })
              .receipt_account_code ?? undefined,
          };
        }
      }
      return next;
    });
  }, [docQuery.data]);
  useEffect(() => {
    if (sourceReceiptQ.data) {
      setRiCache((c) => {
        const next = { ...c };
        for (const it of sourceReceiptQ.data!.items) {
          if (it.id) {
            next[it.id] = {
              nom: it.nomenclature_id,
              qty: it.quantity,
              amount: it.amount_without_vat,
              rcpNum: sourceReceiptQ.data!.document_number,
              rcpId: sourceReceiptQ.data!.id,
              rcpSupplier: contractorNameById.get(
                sourceReceiptQ.data!.contractor_id) ?? undefined,
              rcpAcc: (it as { account_code?: string | null })
                .account_code ?? undefined,
            };
          }
        }
        return next;
      });
    }
  }, [sourceReceiptQ.data]);

  const totalBase = form.items.reduce((s, e) => s + Number(e.distribution_base || 0), 0);
  const totalAmount = Number(form.amount_without_vat || 0) + Number(form.vat_amount || 0);

  // Превью розподілу (для UX перед проведенням)
  const allocatedPreview = useMemo(() => {
    const amt = Number(form.amount_without_vat || 0);
    if (amt <= 0 || totalBase <= 0) return form.items.map(() => 0);
    const result: number[] = [];
    let acc = 0;
    for (let i = 0; i < form.items.length - 1; i++) {
      const it = form.items[i]!;
      const share = Math.round((amt * Number(it.distribution_base || 0) / totalBase) * 10000) / 10000;
      result.push(share);
      acc += share;
    }
    result.push(Math.round((amt - acc) * 10000) / 10000);
    return result;
  }, [form.items, form.amount_without_vat, totalBase]);

  const create = useMutation({
    mutationFn: () => additionalExpenses.create(buildPayload(form)),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['additional-expenses'] });
      navigate(`/additional-expenses/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => additionalExpenses.update(id!, buildPayload(form)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['additional-expense', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await additionalExpenses.create(buildPayload(form));
      try {
        await additionalExpenses.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/additional-expenses/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['additional-expenses'] });
      navigate(`/additional-expenses/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: спершу зберегти правки, потім провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await additionalExpenses.update(id!, buildPayload(form));
      await additionalExpenses.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additional-expense', id] });
      qc.invalidateQueries({ queryKey: ['additional-expenses'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => additionalExpenses.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additional-expense', id] });
      qc.invalidateQueries({ queryKey: ['additional-expenses'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => additionalExpenses.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['additional-expenses'] });
      navigate('/additional-expenses');
    },
  });

  function setItem(i: number, patch: Partial<AdditionalExpenseItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }
  // 2026-07-31 (рішення власника): усі рядки прихода одним кліком.
  function addAllReceiptItems() {
    const rc = sourceReceiptQ.data;
    if (!rc) return;
    setForm((f) => {
      const fresh = rc.items.filter((ri) => ri.id
        && !f.items.some((x) => x.goods_receipt_item_id === ri.id));
      return {
        ...f,
        // 2026-08-02: prefill постачальника/договору з прихода, якщо
        // порожні (часто витрати виставляє той самий постачальник).
        contractor_id: f.contractor_id || rc.contractor_id,
        contract_id: f.contract_id || (rc.contract_id ?? ''),
        items: [...f.items, ...fresh.map((ri, k) => ({
          line_number: f.items.length + k + 1,
          goods_receipt_item_id: ri.id!,
          distribution_base: f.distribute_by === 'QUANTITY'
            ? ri.quantity : ri.amount_without_vat,
        }))],
      };
    });
  }
  // «створити дод. витрати» з прихода (?receipt=): рядки прихода
  // підставляються ОДРАЗУ (раніше приходила порожня таблиця — «криво»).
  const receiptRowsApplied = useRef(false);
  useEffect(() => {
    if (!isNew || !receiptParam || receiptRowsApplied.current) return;
    if (!sourceReceiptQ.data || sourceReceiptQ.data.id !== receiptParam) return;
    receiptRowsApplied.current = true;
    if (form.items.length === 0) addAllReceiptItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, receiptParam, sourceReceiptQ.data]);

  function onVatRateChange(rid: string) {
    const r = vatList.data?.find((v) => v.id === rid);
    const rate = r?.rate_percent ?? 0;
    const amt = Number(form.amount_without_vat || 0);
    setForm({
      ...form,
      vat_rate_id: rid,
      vat_amount: ((amt * rate) / 100).toFixed(2),
    });
  }
  function onAmountChange(v: string) {
    const r = vatList.data?.find((x) => x.id === form.vat_rate_id);
    const rate = r?.rate_percent ?? 0;
    setForm({
      ...form,
      amount_without_vat: v,
      vat_amount: ((Number(v || 0) * rate) / 100).toFixed(2),
    });
  }

  // UX β: warning якщо новий документ а user не має purchases.receipt.create у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати додаткові витрати у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/additional-expenses')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.864: «ще дії» → права колонка: платіжка / РКО постачальнику послуги
  // (moneyDocActions, ч.740) — збережено.
  const railActions = moneyDocActions({ navigate, isNew, direction: 'out', prefill: {
    org: form.organization_id, contractor: form.contractor_id, contract: form.contract_id,
    project: form.project_id, op: 'TO_SUPPLIER',
    amount: Number(form.amount_without_vat || 0) + Number(form.vat_amount || 0),
    purpose: `Оплата за послуги згідно з документом № ${form.supplier_doc_number || form.document_number} від ${fmtDocDate(form.supplier_doc_date || form.document_date)}${vatTail(Number(form.vat_amount || 0))}`,
  } });
  // ч.864: прогрес — ЛИШЕ обовʼязкові поля («Інше» вимагає опису витрати).
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'party', title: 'Постачальник і витрата',
      required: form.expense_type === 'OTHER' ? 2 : 1,
      filled: (form.contractor_id ? 1 : 0)
        + (form.expense_type === 'OTHER' ? (form.comment.trim() ? 1 : 0) : 0) },
    { id: 'sums', title: 'Суми', required: 1,
      filled: Number(form.amount_without_vat || 0) > 0 ? 1 : 0 },
    { id: 'items', title: 'Розподіл по рядках надходження', required: 1,
      filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      {/* ч.864 (новий дизайн, Закон #10): блоки + права рейка. */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нові додаткові витрати' : `Дод. витрати: ${form.document_number}`}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/additional-expenses')}
          onSave={() => {
            if (form.expense_type === 'OTHER' && !form.comment.trim()) {
              setError('Для виду «Інше» опишіть витрату (поле біля виду витрати).');
              return;
            }
            (isNew ? create.mutate() : update.mutate());
          }}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postDisabled={
            isNew
              ? create.isPending || createAndPost.isPending
              : updateAndPost.isPending || update.isPending || form.items.length === 0
          }
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/additional-expenses/${id}/print`)}
          entriesDoc={{ type: 'doc.additional_expenses', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/additional-expenses/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.additional_expenses', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Організація *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        <ProjectField span={3}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>

      </DocBlock>

      <DocBlock index={2} title="Постачальник і витрата">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Постачальник послуги *
            <QuickCreateLink title="Створити нового контрагента"
              disabled={isPosted}
              onClick={() => setQuickContractor(true)} />
          </label>
          <SearchSelect
            items={(contractorsQ.data?.items ?? []).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={form.contractor_id}
            onChange={(cid) => setForm({ ...form, contractor_id: cid, contract_id: '' })}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Договір</label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.contractor_id}>
            <option value="">—</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Вид витрати</label>
          <select value={form.expense_type}
                  onChange={(e) => setForm({ ...form, expense_type: e.target.value })}
                  disabled={isPosted}>
            <option value="TRANSPORT">Транспорт</option>
            <option value="INSURANCE">Страхування</option>
            <option value="CUSTOMS_BROKER">Митний брокер</option>
            <option value="OTHER">Інше</option>
          </select>
          {/* «Інше» вимагає опису (зберігається у коментар). */}
          {form.expense_type === 'OTHER' && (
            <input value={form.comment} placeholder="опишіть витрату *"
              style={{ marginTop: 4 }}
              disabled={isPosted}
              onChange={(e) => setForm({ ...form, comment: e.target.value })} />
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>№ акту</label>
          <input value={form.supplier_doc_number}
                 onChange={(e) => setForm({ ...form, supplier_doc_number: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Дата акту</label>
          <DatePopover value={form.supplier_doc_date}
                 onChange={(v) => setForm({ ...form, supplier_doc_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      </DocBlock>

      <DocBlock index={3} title="Суми">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Сума послуги без ПДВ *</label>
          <input type="number" step="0.01" value={form.amount_without_vat}
                 onChange={(e) => onAmountChange(e.target.value)}
                 disabled={isPosted}
                 style={{ textAlign: 'right' }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Ставка ПДВ</label>
          <select value={form.vat_rate_id}
                  onChange={(e) => onVatRateChange(e.target.value)}
                  disabled={isPosted}>
            <option value="">— (без ПДВ)</option>
            {vatList.data?.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Сума ПДВ</label>
          <input type="number" step="0.01" value={form.vat_amount}
                 onChange={(e) => setForm({ ...form, vat_amount: e.target.value })}
                 disabled={isPosted}
                 style={{ textAlign: 'right' }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Усього</label>
          <input value={fmt(totalAmount)} readOnly
                 style={{ textAlign: 'right', background: 'var(--bg-soft-2)' }} />
        </div>
      </div>

      {receiptPickerOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--overlay)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={() => setReceiptPickerOpen(false)}>
          <div className="card" style={{ width: 640, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
               onClick={(e) => e.stopPropagation()}>
            <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Проведені надходження</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setReceiptPickerOpen(false)}>закрити</button>
            </div>
            <div className="field" style={{ maxWidth: 320, marginBottom: '0.5rem' }}>
              <label>Постачальник (фільтр)</label>
              <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <SearchSelect
                    items={[...new Map((receiptsQ.data?.items ?? [])
                      .filter((r) => r.contractor_id)
                      .map((r) => [r.contractor_id,
                        contractorNameById.get(r.contractor_id) ?? r.contractor_id]))
                      .entries()].map(([cid, name]) => ({ id: cid, label: name }))}
                    value={pickerSupplier}
                    onChange={(cid) => setPickerSupplier(cid)}
                    placeholder="усі постачальники"
                  />
                </div>
                {pickerSupplier && (
                  <button type="button" style={{ padding: '0 0.45rem' }}
                    onClick={() => setPickerSupplier('')}
                    title="Скинути фільтр">×</button>
                )}
              </div>
            </div>
            {(receiptsQ.data?.items.length ?? 0) === 0 && (
              <p style={{ color: 'var(--muted)' }}>Немає проведених надходжень.</p>
            )}
            <table className="tch">
              <tbody>
                {(receiptsQ.data?.items ?? [])
                  .filter((r) => !pickerSupplier || r.contractor_id === pickerSupplier)
                  .map((r) => (
                  <tr key={r.id} style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-soft)' }}
                      onClick={() => {
                        setForm((f) => ({ ...f, source_receipt_id: r.id }));
                        pendingAddRef.current = r.id;
                        setReceiptPickerOpen(false);
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                    <td>{r.document_number}</td>
                    <td>
                      {new Date(r.document_date).toLocaleDateString('uk-UA')}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>
                      {contractorNameById.get(r.contractor_id) ?? '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {fmt(Number(r.total_amount))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      </DocBlock>

      <DocBlock index={4} title="Розподіл по рядках надходження"
        actions={!isPosted ? (
          <button type="button" className="btn-doc"
            onClick={() => setReceiptPickerOpen(true)}
            title="Обрати проведене надходження: рядки товарів додадуться у розподіл. Можна додати рядки кількох надходжень — зайві приберіть ×">
            <DocIcon />додати рядки з надходження
          </button>
        ) : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>Розподіляти:</span>
        <select value={form.distribute_by} disabled={isPosted}
          style={{ fontSize: '0.85rem', fontWeight: 400, width: 'auto' }}
          onChange={(e) => {
            const mode = e.target.value as 'AMOUNT' | 'QUANTITY';
            // Зміна режиму перераховує бази наявних рядків з даних прихода.
            setForm((f) => ({
              ...f,
              distribute_by: mode,
              items: f.items.map((it) => {
                const ri = riCache[it.goods_receipt_item_id];
                if (!ri) return it;
                return {
                  ...it,
                  distribution_base: mode === 'QUANTITY' ? ri.qty : ri.amount,
                };
              }),
            }));
          }}>
          <option value="AMOUNT">за сумою</option>
          <option value="QUANTITY">за кількістю</option>
        </select>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <span className="seg-tabs">
            <button type="button" className={viewTab === 'accounts' ? 'active' : ''}
              onClick={() => setViewTab(viewTab === 'accounts' ? 'lines' : 'accounts')}>
              Рахунки обліку</button>
          </span>
        </span>
      </div>

      {viewTab === 'accounts' && (
        <div style={{ marginTop: '0.5rem' }}>
          <div className="field" style={{ maxWidth: 340 }}>
            <label title="Кт проводки — розрахунки з постачальником послуги (порожньо — авто: правила контрагента / 631)">
              Рахунок розрахунків (Кт)</label>
            <input value={form.settlement_account_code} maxLength={10}
              onChange={(e) => setForm({ ...form, settlement_account_code: e.target.value })}
              disabled={isPosted}
              placeholder="авто: за правилами / 631" />
          </div>
          <TchTable style={{ marginTop: '0.5rem' }}>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th>Товар</th>
                <th style={{ width: 220 }}
                    title="Дт проводки рядка (порожньо — авто: рахунок рядка надходження, інакше 281)">
                  Рах. обліку (Дт)</th>
              </tr>
            </thead>
            <tbody>
              {form.items.map((it, i) => {
                const ri = riCache[it.goods_receipt_item_id];
                return (
                  <tr key={i}>
                    <td>
                      {ri ? (nomName.get(ri.nom) ?? 'товар') : '—'}
                    </td>
                    <td>
                      <input value={it.account_code ?? ''} maxLength={10}
                        onChange={(e) => setItem(i, { account_code: e.target.value || null })}
                        disabled={isPosted}
                        placeholder={`авто: ${ri?.rcpAcc ?? '281'}`}
                        style={{ width: '100%' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Дт успадковує рахунок рядка надходження (товари 281, матеріали 201,
            обладнання 15х) — впишіть код, щоб перебити. Кт — розрахунки з
            постачальником послуги.
          </p>
        </div>
      )}
      {viewTab === 'lines' && (
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 30 }}>#</th>
            <th>Товар (з надходження)</th>
            <th style={{ width: 180 }}>Постачальник</th>
            <th style={{ width: 150 }}
                title="Документ-прихід рядка (витрати можуть розподілятись на кілька приходів)">
              Прихід</th>
            <th style={{ width: 130 }}>База розподілу</th>
            <th style={{ width: 130 }}>Буде розподілено</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const ri = riCache[it.goods_receipt_item_id];
            const label = ri
              ? `${nomName.get(ri.nom) ?? 'товар'} × ${ri.qty}`
              : it.goods_receipt_item_id.slice(0, 8) + '…';
            const alloc = isPosted
              ? Number(it.allocated_amount ?? 0)
              : (allocatedPreview[i] ?? 0);
            return (
              <tr key={i}>
                <td>{it.line_number}</td>
                <td style={{ color: 'var(--text-secondary)' }}>{label}</td>
                <td style={{ color: 'var(--text-secondary)' }}>
                  {ri?.rcpSupplier ?? '—'}
                </td>
                <td>
                  {ri?.rcpNum ? (
                    <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                      onClick={() => ri.rcpId && navigate(`/goods-receipts/${ri.rcpId}`)}>
                      №{ri.rcpNum}
                    </a>
                  ) : <span style={{ color: 'var(--muted)' }}>—</span>}
                </td>
                <td>
                  <input type="number" step="0.01" value={it.distribution_base}
                         onChange={(e) => setItem(i, { distribution_base: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  {fmt(alloc)}
                </td>
                <td>
                  {!isPosted && (
                    <button onClick={() => removeRow(i)} className="danger"
                            style={{ padding: '0.3rem 0.6rem' }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ textAlign: 'right' }}>Усього:</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalBase)}</td>
            <td style={{ textAlign: 'right' }}>{fmt(Number(form.amount_without_vat || 0))}</td>
            <td></td>
          </tr>
        </tfoot>
      </TchTable>
      )}
      </DocBlock>

      <DocBlock index={5} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.additional_expenses" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}


      {/* ч.948: попапи швидкого створення просто з форми документа. */}
      {quickContractor && (
        <QuickContractorModal
          direction={'OUT'}
          onClose={() => setQuickContractor(false)}
          onCreated={(cid) => {
            setQuickContractor(false);
            setForm((f) => ({ ...f, contractor_id: cid, contract_id: '' }));
            qc.invalidateQueries({ queryKey: ['contractors'] });
          }} />
      )}
      {quickContract && form.contractor_id && (
        <QuickContractModal
          orgId={form.organization_id}
          contractorId={form.contractor_id}
          contractorName={contractorNameForQuick}
          direction={'OUT'}
          onClose={() => setQuickContract(false)}
          onCreated={(cid) => {
            setQuickContract(false);
            setForm((f) => ({ ...f, contract_id: cid }));
            qc.invalidateQueries({ queryKey: ['contracts', form.organization_id, form.contractor_id] });
          }} />
      )}
    </div>
  );
}

function buildPayload(form: FormState) {
  return {
    organization_id: form.organization_id,
    document_number: form.document_number,
    document_date: new Date(form.document_date).toISOString(),
    contractor_id: form.contractor_id,
    contract_id: form.contract_id,
    expense_type: form.expense_type,
    distribute_by: form.distribute_by,
    supplier_doc_number: form.supplier_doc_number || null,
    supplier_doc_date: form.supplier_doc_date || null,
    amount_without_vat: form.amount_without_vat || '0',
    vat_rate_id: form.vat_rate_id || null,
    vat_amount: form.vat_amount || '0',
    comment: form.comment || null,
    settlement_account_code: form.settlement_account_code || null,
    items: form.items.map((it) => ({
      line_number: it.line_number,
      goods_receipt_item_id: it.goods_receipt_item_id,
      distribution_base: it.distribution_base,
    })),
  };
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
